import type { TAPIError } from '@myua/api';
import { usd } from './aiApi';

// Single place that turns a failed `/api/asset-lab/ai/*` call into a sentence we
// can put in front of the user. Every caller (useAiKeys, backendProvider) goes
// through describeAiError — the backend's `detail` comes back in three different
// shapes and interpolating it blindly yields "[object Object]".
//
//   400 / 403 / 413 / 502 → detail is a plain string
//                           ("Unknown model: …", "API key not found",
//                            "Asset 42 is not an image",
//                            "Too many reference images, max is 8",
//                            "Image is too large, max size is 5 MB",
//                            "AI generation is disabled", and for 502 the raw
//                            provider text from Gemini/OpenAI, passed through).
//   422                  → detail is FastAPI's validation array
//                           [{ loc, msg, type }, …]
//   429                  → detail is a budget object
//                           { code: 'limit_exceeded', message?, scope,
//                             limit_usd, spent_usd, estimated_cost_usd }
//
// NB: @myua/api types TAPIError.error as `z.string()`, but it is assigned
// `response.detail` verbatim (request.ts), so for 422/429 it is *not* a string.
// Always read `response.detail`, never `error`.
//
// 403 with "Authentication credentials were not provided." never reaches us —
// @myua/api redirects to the login page first (fetchFn.ts).
//
// One 413 does *not* come from the API at all: when the JSON body (inline base64
// references) exceeds the ingress body cap, nginx answers with its own HTML error
// page and the request never reaches FastAPI, so there is no `detail` to read —
// only the status. Without the special case below that lands on the generic
// fallback ("Generation failed"), which tells the user nothing about the actual
// cause. The client keeps payloads under the cap (refCompression); this message
// covers what still gets through.

// FastAPI validation entry (422).
type ValidationDetail = { loc?: unknown; msg?: string; type?: string };

// Budget rejection (429). `scope` says whose budget ran out: the caller's
// personal sub-limit or the shared key's monthly cap. `message` is the
// backend's own human-readable wording — when present it is shown verbatim
// (limits are managed by admins in Django Admin, so the backend's text is the
// authority on what happened and whom to ask).
type LimitDetail = {
  code: 'limit_exceeded';
  message?: string;
  scope?: 'user' | 'api_key' | string;
  limit_usd?: number;
  spent_usd?: number;
  estimated_cost_usd?: number;
};

function isLimitDetail(detail: unknown): detail is LimitDetail {
  return (
    !!detail &&
    typeof detail === 'object' &&
    !Array.isArray(detail) &&
    (detail as { code?: unknown }).code === 'limit_exceeded'
  );
}

function describeLimit(detail: LimitDetail): string {
  // Prefer the backend's own sentence; the assembled fallback covers older
  // responses that carry only the structured fields.
  if (typeof detail.message === 'string' && detail.message.trim()) {
    return detail.message.trim();
  }
  const budget = `spent ${usd(detail.spent_usd)} of ${usd(detail.limit_usd)}, this generation costs about ${usd(detail.estimated_cost_usd)}.`;
  return detail.scope === 'api_key'
    ? `The key's monthly budget is used up — ${budget}`
    : `Your monthly budget is used up — ${budget}`;
}

function describeValidation(detail: ValidationDetail[]): string {
  const messages = detail
    .map((d) => (typeof d?.msg === 'string' ? d.msg.trim() : ''))
    .filter((msg) => msg.length > 0);
  return messages.length > 0 ? messages.join('; ') : 'The request was rejected as invalid.';
}

// `err` is whatever `safeShortcuts` handed back in its `data` on failure — a
// TAPIError in practice, but typed `unknown` so callers can pass a caught value
// straight through. `fallback` overrides the generic last-resort sentence.
export function describeAiError(err: unknown, fallback?: string): string {
  const apiError = (err ?? {}) as Partial<TAPIError>;
  const status = apiError.status ?? null;

  // fetch itself never completed (offline, DNS, aborted socket).
  if (status === null) {
    return 'Could not reach the server. Check your connection and try again.';
  }

  const response: unknown = apiError.response;
  const detail: unknown =
    response && typeof response === 'object' && !Array.isArray(response)
      ? (response as { detail?: unknown }).detail
      : undefined;

  // Order matters: the object/array shapes must be recognised *before* anything
  // stringifies `detail`, otherwise 422/429 render as "[object Object]".
  if (Array.isArray(detail)) {
    return describeValidation(detail as ValidationDetail[]);
  }
  if (isLimitDetail(detail)) {
    return describeLimit(detail);
  }
  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  // Rejected by the ingress, not the API (see the note above): no detail, just
  // the status. Name the cause the user can act on.
  if (status === 413) {
    return 'The references are too heavy for one request. Remove one, or use lighter images.';
  }

  // A 5xx with no `detail` is the backend breaking on a request it accepted —
  // nothing the user did wrong and nothing they can fix by rewording. Say the
  // status: a bare "try again" is indistinguishable from every other failure,
  // and the status is what turns a report into something reproducible (the
  // backend logs the real reason against the request — GET /ai/requests/).
  if (status >= 500) {
    return `${fallback ?? 'The request failed.'} The server answered ${status}.`;
  }

  return fallback ?? `The request failed (${status}). Please try again.`;
}
